import React, { useState, useEffect } from 'react';
import { X, Calendar, MapPin, DollarSign, Settings, Shield, FileText, Image as ImageIcon, CheckCircle, XCircle, Loader2, Info, Car, Fuel, Gauge, Users, Tag, Type as TypeIcon } from 'lucide-react';
import vehicleService from '../../../services/vehicleService';
import rejectionReasonService from '../../../services/rejectionReasonService';
import { ROUTES } from '../../../constants/routes.constants';
import { getImageUrl } from '../../../utils/imageUtils';
import { formatCategoryLabel, formatVehicleCategoriesFromDoc } from '../../../utils/vehicleCategoryDisplay';
import {
  buildRejectionMessageFromReason,
  DEFAULT_REJECTION_TEMPLATE,
  fillRejectionTemplate,
} from '../../../utils/rejectionTemplate';

const generateRejectionTemplate = (vehicle) => fillRejectionTemplate(DEFAULT_REJECTION_TEMPLATE, vehicle);

const VehicleDetailsModal = ({ vehicleId, isOpen, onClose, onVerify, onReject, onResetRejection }) => {
  const [vehicle, setVehicle] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionMessage, setRejectionMessage] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectError, setRejectError] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [selectedRejectionReasonId, setSelectedRejectionReasonId] = useState('');

  const handleExportPdf = async () => {
    try {
      setIsExporting(true);
      const token = localStorage.getItem('accessToken');
      
      const response = await fetch(`${ROUTES.API_BASE_URL}/v1/vehicles/${vehicleId}/export-pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de la génération du PDF');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const newWindow = window.open(url, '_blank');
      
      if (!newWindow) {
         const a = document.createElement('a');
         a.href = url;
         a.download = `fiche-vehicule-${vehicle?.licencePlateNumber || vehicleId}.pdf`;
         document.body.appendChild(a);
         a.click();
         a.remove();
      }
    } catch (error) {
      console.error('Export error:', error);
      alert(error.message || "Impossible de générer le document PDF.");
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    if (isOpen && vehicleId) {
      loadVehicleDetails();
    }
  }, [isOpen, vehicleId]);

  // Désactiver le scroll du body quand le modal est ouvert
  useEffect(() => {
    if (isOpen || showRejectModal) {
      // Sauvegarder la position actuelle du scroll
      const scrollY = window.scrollY;
      // Désactiver le scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      // Réactiver le scroll quand le modal est fermé
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen, showRejectModal]);

  const applyRejectionReason = (reasonId, reasonsList = rejectionReasons) => {
    setSelectedRejectionReasonId(reasonId);
    if (!reasonId) {
      setRejectionMessage(generateRejectionTemplate(vehicle));
      return;
    }
    const reason = reasonsList.find((item) => String(item._id) === String(reasonId));
    setRejectionMessage(buildRejectionMessageFromReason(reason, vehicle));
  };

  const openRejectModal = async () => {
    setRejectError('');
    setSelectedRejectionReasonId('');
    setRejectionMessage(generateRejectionTemplate(vehicle));
    setShowRejectModal(true);
    try {
      const response = await rejectionReasonService.getAll({
        limit: 200,
        status: 'active',
        sortBy: 'title',
        sortOrder: 'asc',
      });
      const list = response?.data || [];
      setRejectionReasons(list);
      if (list.length === 1) {
        applyRejectionReason(list[0]._id, list);
      }
    } catch (err) {
      console.error('Error loading rejection reasons:', err);
      setRejectionReasons([]);
    }
  };

  const loadVehicleDetails = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await vehicleService.getVehicleById(vehicleId);
      if (response.success) {
        setVehicle(response.data);
      } else {
        setError('Impossible de charger les détails du véhicule');
      }
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des détails');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const isOtherManualModel = (v) =>
    /^autre$/i.test(String(v?.specs?.model?.name ?? '').trim()) &&
    String(v?.customModelName ?? '').trim().length > 0;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (isActive, isVerified, isFeatured) => {
    const badges = [];
    if (isActive) {
      badges.push(
        <span key="active" className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
          Actif
        </span>
      );
    } else {
      badges.push(
        <span key="inactive" className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
          Inactif
        </span>
      );
    }
    if (isVerified) {
      badges.push(
        <span key="verified" className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full flex items-center gap-1">
          <Shield className="w-3 h-3" />
          Vérifié
        </span>
      );
    } else {
      badges.push(
        <span key="not-verified" className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full flex items-center gap-1">
          <Shield className="w-3 h-3" />
          Non vérifié
        </span>
      );
    }
    if (isFeatured) {
      badges.push(
        <span key="featured" className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full flex items-center gap-1">
          ⭐ En vedette
        </span>
      );
    }
    return <div className="flex flex-wrap gap-2">{badges}</div>;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
        onClick={onClose}
      />

      {/* Modal - Centered and fixed */}
      <div className="relative bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-[#27489E] px-6 py-4 flex items-center justify-between flex-shrink-0">
          <h3 className="text-lg font-semibold text-white">Détails du véhicule</h3>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content - Scrollable inside modal */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
            {isLoading ? (
              <div className="py-12 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#27489E]"></div>
                <p className="mt-4 text-gray-500">Chargement des détails...</p>
              </div>
            ) : error ? (
              <div className="py-12 text-center">
                <p className="text-red-500">{error}</p>
              </div>
            ) : vehicle ? (
              <div className="space-y-6">
                {/* Status Badges */}
                <div>
                  {getStatusBadge(vehicle.isActive, vehicle.isVerified, vehicle.isFeatured)}
                </div>

                {/* Images */}
                
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <ImageIcon className="w-4 h-4" />
                    Photos du véhicule
                    {vehicle.images && Array.isArray(vehicle.images) && vehicle.images.length > 0 && (
                      <span className="text-xs text-gray-500 font-normal">({vehicle.images.length})</span>
                    )}
                  </h4>
                  {vehicle.images && Array.isArray(vehicle.images) && vehicle.images.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {vehicle.images.map((image, index) => {
                        if (!image || image.trim() === '') return null;
                        // Convert relative URLs to absolute if needed
                        const imageUrl = getImageUrl(image);
                        return (
                          <div key={index} className="relative group">
                            <img
                              src={imageUrl}
                              alt={`Véhicule ${index + 1}`}
                              className="w-full h-40 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition"
                              onError={(e) => {
                                console.error('Error loading image:', imageUrl);
                                e.target.src = 'https://via.placeholder.com/300x200?text=Image+non+disponible';
                              }}
                              onClick={() => window.open(imageUrl, '_blank')}
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition flex items-center justify-center rounded-lg pointer-events-none">
                              <span className="text-white opacity-0 group-hover:opacity-100 text-xs bg-black bg-opacity-50 px-2 py-1 rounded">Cliquer pour agrandir</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-8 bg-gray-50 rounded-lg text-center border-2 border-dashed border-gray-300">
                      <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">Aucune photo disponible</p>
                      <p className="text-xs text-gray-400 mt-1">Les photos n'ont pas encore été uploadées</p>
                    </div>
                  )}
                </div>
                

                {/* Offres spéciales (Remises) */}
                {(vehicle.pricing?.weeklyDiscount || vehicle.pricing?.monthlyDiscount) && (
                  <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 shadow-sm">
                    <h4 className="text-sm font-semibold text-indigo-900 mb-2 flex items-center gap-2">
                      <Tag className="w-4 h-4 text-indigo-600" />
                      Offres spéciales
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {vehicle.pricing?.weeklyDiscount && (
                        
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-indigo-700 rounded-full text-xs font-semibold shadow-sm border border-indigo-100">
                          {vehicle.pricing.weeklyDiscount.type === 'PERCENTAGE'
                            ? `🔥 Remise 7j : ${vehicle.pricing.weeklyDiscount.value}%`
                            : `🔥 Remise 7j : ${vehicle.pricing.weeklyDiscount.value} ${vehicle.pricing?.currency || 'MAD'}`}
                        </span>
                        
                      )}
                      {vehicle.pricing?.monthlyDiscount && (
                        
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-emerald-700 rounded-full text-xs font-semibold shadow-sm border border-emerald-100">
                          {vehicle.pricing.monthlyDiscount.type === 'PERCENTAGE'
                            ? `🔥 Remise 30j : ${vehicle.pricing.monthlyDiscount.value}%`
                            : `🔥 Remise 30j : ${vehicle.pricing.monthlyDiscount.value} ${vehicle.pricing?.currency || 'MAD'}`}
                        </span>
                        
                      )}
                    </div>
                  </div>
                )}

                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Informations de base</h4>
                    <div className="space-y-2 text-sm">
                      
                      <div>
                        <span className="text-gray-500">Nom du véhicule:</span>
                        <span className="ml-2 font-medium text-gray-900">{vehicle.title || 'N/A'}</span>
                      </div>
                      
                      <div>
                        <span className="text-gray-500">Description:</span>
                        <p className="mt-1 text-gray-900">{vehicle.description || 'Aucune description'}</p>
                      </div>
                      
                      <div>
                        <span className="text-gray-500">Numéro de plaque:</span>
                        <span className="ml-2 font-medium text-gray-900">{vehicle.licencePlateNumber || 'N/A'}</span>
                      </div>
                      
                      <div>
                        <span className="text-gray-500">Type:</span>
                        <span className="ml-2 font-medium text-gray-900">
                          {vehicle.type || formatCategoryLabel(vehicle.vehicleType) || 'N/A'}
                        </span>
                      </div>
                      
                      <div>
                        <span className="text-gray-500">Catégorie:</span>
                        <span className="ml-2 font-medium text-gray-900">
                          {formatVehicleCategoriesFromDoc(vehicle)}
                        </span>
                      </div>
                      
                      <div>
                        <span className="text-gray-500">Prix journalier:</span>
                        <span className="ml-2 font-medium text-gray-900">
                          {vehicle.pricing?.basePrice != null
                            ? `${Number(vehicle.pricing.basePrice).toLocaleString('fr-FR')} ${vehicle.pricing?.currency || 'MAD'}`
                            : 'N/A'}
                        </span>
                      </div>
                      
                      <div>
                        <span className="text-gray-500">Caution:</span>
                        <span className="ml-2 font-medium text-gray-900">
                          {vehicle.pricing?.deposit?.value != null
                            ? `${Number(vehicle.pricing.deposit.value).toLocaleString('fr-FR')} ${vehicle.pricing?.currency || 'MAD'}`
                            : 'N/A'}
                        </span>
                      </div>
                      
                      <div>
                        <span className="text-gray-500">Confirmation de réservation:</span>
                        <span className="ml-2 font-medium text-gray-900">{vehicle.bookingConfirmation || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Technical Specs */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                      <Settings className="w-4 h-4" />
                      Spécifications techniques
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {/* Marque */}
                      
                      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl shadow-sm border border-gray-100">
                        <div className="bg-white p-2 rounded-full shadow-sm text-[#27489E]">
                          <Car className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Marque</p>
                          <p className="font-bold text-gray-900">{vehicle.specs?.brand?.name || vehicle.customMakeName || 'N/A'}</p>
                        </div>
                      </div>
                      
                      
                      {/* Modèle */}
                      
                      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl shadow-sm border border-gray-100">
                        <div className="bg-white p-2 rounded-full shadow-sm text-[#27489E]">
                          <TypeIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Modèle</p>
                          <div className="flex flex-wrap items-baseline gap-1">
                            {isOtherManualModel(vehicle) ? (
                              <div className="flex items-center gap-1">
                                <span className="font-bold text-gray-900">{vehicle.customModelName}</span>
                                <span className="flex items-center bg-sky-100 text-sky-800 px-1.5 py-0.5 rounded text-[10px]" title="Saisie manuelle">
                                  Autre
                                </span>
                              </div>
                            ) : (
                              <p className="font-bold text-gray-900">{vehicle.displayModelName || vehicle.specs?.model?.name || 'N/A'}</p>
                            )}
                          </div>
                        </div>
                      </div>
                      

                      {/* Année */}
                      
                      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl shadow-sm border border-gray-100">
                        <div className="bg-white p-2 rounded-full shadow-sm text-[#27489E]">
                          <Calendar className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Année</p>
                          <p className="font-bold text-gray-900">{vehicle.specs?.year || 'N/A'}</p>
                        </div>
                      </div>
                      

                      {/* Transmission */}
                      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl shadow-sm border border-gray-100">
                        <div className="bg-white p-2 rounded-full shadow-sm text-[#27489E]">
                          <Settings className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Transmission</p>
                          <p className="font-bold text-gray-900">{vehicle.specs?.transmission === 'MANUAL' ? 'Manuelle' : (vehicle.specs?.transmission || 'N/A')}</p>
                        </div>
                      </div>
                      

                      {/* Carburant */}
                      
                      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl shadow-sm border border-gray-100">
                        <div className="bg-white p-2 rounded-full shadow-sm text-[#27489E]">
                          <Fuel className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Carburant</p>
                          <p className="font-bold text-gray-900">{vehicle.specs?.fuel?.name || 'N/A'}</p>
                        </div>
                      </div>
                      

                      {/* Odomètre */}
                      
                      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl shadow-sm border border-gray-100">
                        <div className="bg-white p-2 rounded-full shadow-sm text-[#27489E]">
                          <Gauge className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Kilométrage</p>
                          <p className="font-bold text-gray-900">{vehicle.specs?.odometer?.name || (typeof vehicle.specs?.odometer === 'string' ? vehicle.specs.odometer : null) || 'N/A'}</p>
                        </div>
                      </div>
                      

                      {/* Seats */}
                      
                      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl shadow-sm border border-gray-100">
                        <div className="bg-white p-2 rounded-full shadow-sm text-[#27489E]">
                          <Users className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Places</p>
                          <p className="font-bold text-gray-900">{vehicle.specs?.seats || 'N/A'}</p>
                        </div>
                      </div>
                      
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Localisation
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    
                      <div>
                        <span className="text-gray-500">Ville:</span>
                        <span className="ml-2 font-medium text-gray-900">{vehicle.location?.city || vehicle.vehicleLocation?.cityName || 'N/A'}</span>
                      </div>
                    
                      <div>
                        <span className="text-gray-500">Pays:</span>
                        <span className="ml-2 font-medium text-gray-900">{vehicle.vehicleLocation?.country || 'N/A'}</span>
                      </div>
                    <div className="md:col-span-2">
                      
                      <div>
                        <span className="text-gray-500">Adresse complète:</span>
                        <p className="ml-2 font-medium text-gray-900">{vehicle.location?.address || 'N/A'}</p>
                      </div>
                    </div>
                    {vehicle.location?.coordinates && (
                      <div className="md:col-span-2">
                        
                      <div>
                        <span className="text-gray-500">Coordonnées:</span>
                        <span className="ml-2 font-medium text-gray-900">
                            {vehicle.location.coordinates[1]}, {vehicle.location.coordinates[0]}
                          </span>
                      </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Features */}
                {vehicle.features && vehicle.features.length > 0 && (
                  
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Équipements</h4>
                    <div className="flex flex-wrap gap-2">
                      {vehicle.features.map((feature) => (
                        <span
                          key={feature._id || feature}
                          className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
                        >
                          {typeof feature === 'object' ? feature.name : feature}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                )}

                {/* Rules */}
                {vehicle.rules && vehicle.rules.length > 0 && (
                  
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Règles du véhicule</h4>
                    <div className="flex flex-wrap gap-2">
                      {vehicle.rules.map((rule) => (
                        <span
                          key={rule._id || rule}
                          className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                        >
                          {typeof rule === 'object' ? rule.name : rule}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                )}

                {/* Cancellation Policies */}
                {vehicle.cancellationPolicies && vehicle.cancellationPolicies.length > 0 && (
                  
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Politiques d'annulation</h4>
                    <div className="space-y-2">
                      {vehicle.cancellationPolicies.map((policy, index) => {
                        // Replace x% or x in description with the custom percentage
                        let description = typeof policy.policy === 'object' && policy.policy.description 
                          ? policy.policy.description 
                          : '';
                        
                        if (description) {
                          // Replace x% with the actual percentage
                          description = description.replace(/x%/gi, `${policy.percentage}%`);
                          // Replace standalone x (not part of a word) with the percentage
                          description = description.replace(/\bx\b/gi, `${policy.percentage}`);
                        }
                        
                        return (
                          <div key={index} className="p-3 bg-gray-50 rounded-lg">
                            <div className="font-medium text-gray-900">
                              {typeof policy.policy === 'object' ? policy.policy.name : policy.policy}
                            </div>
                            {description && (
                              <p className="text-sm text-gray-600 mt-1">{description}</p>
                            )}
                            <div className="text-sm text-gray-500 mt-1">
                              Pourcentage personnalisé: {policy.percentage}%
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                )}

                {/* Rental Rules */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Règles de location</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    
                      <div>
                        <span className="text-gray-500">Jours minimum:</span>
                        <span className="ml-2 font-medium text-gray-900">{vehicle.minRentalDays || 'N/A'}</span>
                      </div>
                    
                      <div>
                        <span className="text-gray-500">Couverture d'assurance:</span>
                        <span className="ml-2 font-medium text-gray-900">{vehicle.insuranceCoverage || 'N/A'}</span>
                      </div>
                    
                      <div>
                        <span className="text-gray-500">Âge minimum:</span>
                        <span className="ml-2 font-medium text-gray-900">
                        {vehicle.ageRestriction?.enabled
                          ? `${vehicle.ageRestriction.minimumAge} ans`
                          : 'Non'}
                      </span>
                      </div>
                    
                      <div>
                        <span className="text-gray-500">Fumer autorisé:</span>
                        <span className="ml-2 font-medium text-gray-900">
                        {vehicle.smokingAllowed ? 'Oui' : 'Non'}
                      </span>
                      </div>
                    
                      <div>
                        <span className="text-gray-500">Voyage international:</span>
                        <span className="ml-2 font-medium text-gray-900">
                        {vehicle.internationalTravelAllowed ? 'Oui' : 'Non'}
                      </span>
                      </div>
                  </div>
                </div>

                {/* Administrative Documents */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Documents administratifs
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Carte grise recto */}
                    
                    <div>
                      <p className="text-sm text-gray-500 mb-2 font-medium">Carte grise (Recto)</p>
                      {vehicle.registrationCardFront && vehicle.registrationCardFront.trim() !== '' ? (
                        <div className="relative group">
                          {(() => {
                            const docUrl = getImageUrl(vehicle.registrationCardFront) || vehicle.registrationCardFront;
                            const isPdf = docUrl.toLowerCase().includes('.pdf') || docUrl.toLowerCase().includes('application/pdf');
                            
                            return isPdf ? (
                              <a
                                href={docUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block p-6 bg-red-50 rounded-lg text-center hover:bg-red-100 transition cursor-pointer border-2 border-red-200"
                              >
                                <FileText className="w-10 h-10 text-red-500 mx-auto mb-2" />
                                <p className="text-xs font-medium text-gray-700">Document PDF</p>
                                <p className="text-xs text-gray-500 mt-1">Cliquer pour ouvrir</p>
                              </a>
                            ) : (
                              <div className="relative">
                                <img
                                  src={docUrl}
                                  alt="Carte grise recto"
                                  className="w-full h-48 object-contain rounded-lg border-2 border-gray-200 bg-gray-50 cursor-pointer hover:border-[#27489E] transition"
                                  onClick={() => window.open(docUrl, '_blank')}
                                  onError={(e) => {
                                    console.error('Error loading document:', docUrl);
                                    e.target.style.display = 'none';
                                    const parent = e.target.parentElement;
                                    parent.innerHTML = `
                                      <div class="p-6 bg-gray-50 rounded-lg text-center border-2 border-dashed border-gray-300">
                                        <svg class="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                        </svg>
                                        <p class="text-xs text-gray-500">Document non disponible</p>
                                        <p class="text-xs text-gray-400 mt-1">L'URL du document est invalide</p>
                                      </div>
                                    `;
                                  }}
                                />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition flex items-center justify-center rounded-lg pointer-events-none">
                                <span className="text-white opacity-0 group-hover:opacity-100 text-xs bg-black bg-opacity-50 px-3 py-1 rounded">Cliquer pour agrandir</span>
                              </div>
                            </div>
                          );
                          })()}
                        </div>
                      ) : (
                        <div className="p-6 bg-gray-50 rounded-lg text-center border-2 border-dashed border-gray-300">
                          <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-xs text-gray-500">Document non fourni</p>
                        </div>
                      )}
                    </div>
                    

                    {/* Carte grise verso */}
                    
                    <div>
                      <p className="text-sm text-gray-500 mb-2 font-medium">Carte grise (Verso)</p>
                      {vehicle.registrationCardBack && vehicle.registrationCardBack.trim() !== '' ? (
                        <div className="relative group">
                          {(() => {
                            const docUrl = getImageUrl(vehicle.registrationCardBack) || vehicle.registrationCardBack;
                            const isPdf = docUrl.toLowerCase().includes('.pdf') || docUrl.toLowerCase().includes('application/pdf');
                            
                            return isPdf ? (
                              <a
                                href={docUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block p-6 bg-red-50 rounded-lg text-center hover:bg-red-100 transition cursor-pointer border-2 border-red-200"
                              >
                                <FileText className="w-10 h-10 text-red-500 mx-auto mb-2" />
                                <p className="text-xs font-medium text-gray-700">Document PDF</p>
                                <p className="text-xs text-gray-500 mt-1">Cliquer pour ouvrir</p>
                              </a>
                            ) : (
                              <div className="relative">
                                <img
                                  src={docUrl}
                                  alt="Carte grise verso"
                                  className="w-full h-48 object-contain rounded-lg border-2 border-gray-200 bg-gray-50 cursor-pointer hover:border-[#27489E] transition"
                                  onClick={() => window.open(docUrl, '_blank')}
                                  onError={(e) => {
                                    console.error('Error loading document:', docUrl);
                                    e.target.style.display = 'none';
                                    const parent = e.target.parentElement;
                                    parent.innerHTML = `
                                      <div class="p-6 bg-gray-50 rounded-lg text-center border-2 border-dashed border-gray-300">
                                        <svg class="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                        </svg>
                                        <p class="text-xs text-gray-500">Document non disponible</p>
                                        <p class="text-xs text-gray-400 mt-1">L'URL du document est invalide</p>
                                      </div>
                                    `;
                                  }}
                                />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition flex items-center justify-center rounded-lg pointer-events-none">
                                <span className="text-white opacity-0 group-hover:opacity-100 text-xs bg-black bg-opacity-50 px-3 py-1 rounded">Cliquer pour agrandir</span>
                              </div>
                            </div>
                          );
                          })()}
                        </div>
                      ) : (
                        <div className="p-6 bg-gray-50 rounded-lg text-center border-2 border-dashed border-gray-300">
                          <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-xs text-gray-500">Document non fourni</p>
                        </div>
                      )}
                    </div>
                    

                    {/* Autorisation ministère */}
                    
                    <div>
                      <p className="text-sm text-gray-500 mb-2 font-medium">Autorisation ministère</p>
                      {vehicle.ministryAuthorization && vehicle.ministryAuthorization.trim() !== '' ? (
                        <div className="relative group">
                          {(() => {
                            const docUrl = getImageUrl(vehicle.ministryAuthorization) || vehicle.ministryAuthorization;
                            const isPdf = docUrl.toLowerCase().includes('.pdf') || docUrl.toLowerCase().includes('application/pdf');
                            
                            return isPdf ? (
                              <a
                                href={docUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block p-6 bg-blue-50 rounded-lg text-center hover:bg-blue-100 transition cursor-pointer border-2 border-blue-200"
                              >
                                <FileText className="w-10 h-10 text-blue-500 mx-auto mb-2" />
                                <p className="text-xs font-medium text-gray-700">Document PDF</p>
                                <p className="text-xs text-gray-500 mt-1">Cliquer pour ouvrir</p>
                              </a>
                            ) : (
                              <div className="relative">
                                <img
                                  src={docUrl}
                                  alt="Autorisation ministère"
                                  className="w-full h-48 object-contain rounded-lg border-2 border-gray-200 bg-gray-50 cursor-pointer hover:border-[#27489E] transition"
                                  onClick={() => window.open(docUrl, '_blank')}
                                  onError={(e) => {
                                    console.error('Error loading document:', docUrl);
                                    e.target.style.display = 'none';
                                    const parent = e.target.parentElement;
                                    parent.innerHTML = `
                                      <div class="p-6 bg-gray-50 rounded-lg text-center border-2 border-dashed border-gray-300">
                                        <svg class="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                        </svg>
                                        <p class="text-xs text-gray-500">Document non disponible</p>
                                        <p class="text-xs text-gray-400 mt-1">L'URL du document est invalide</p>
                                      </div>
                                    `;
                                  }}
                                />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition flex items-center justify-center rounded-lg pointer-events-none">
                                <span className="text-white opacity-0 group-hover:opacity-100 text-xs bg-black bg-opacity-50 px-3 py-1 rounded">Cliquer pour agrandir</span>
                              </div>
                            </div>
                          );
                          })()}
                        </div>
                      ) : (
                        <div className="p-6 bg-gray-50 rounded-lg text-center border-2 border-dashed border-gray-300">
                          <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-xs text-gray-500">Document non fourni</p>
                        </div>
                      )}
                    </div>
                    
                  </div>
                </div>

                {/* Owner Information */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Propriétaire</h4>
                  <div className="text-sm">
                    <div>
                      <span className="text-gray-500">Nom:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {vehicle.owner?.fullName || vehicle.owner?.email || 'N/A'}
                      </span>
                    </div>
                    {vehicle.owner?.vendorDetails?.companyName && (
                      <div className="mt-1">
                        <span className="text-gray-500">Entreprise:</span>
                        <span className="ml-2 font-medium text-gray-900">
                          {vehicle.owner.vendorDetails.companyName}
                        </span>
                      </div>
                    )}
                    {vehicle.owner?.email && (
                      <div className="mt-1">
                        <span className="text-gray-500">Email:</span>
                        <span className="ml-2 font-medium text-gray-900">{vehicle.owner.email}</span>
                      </div>
                    )}
                  </div>
                </div>
                {/* Created Date */}
                <div className="text-sm text-gray-500">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Créé le: {formatDate(vehicle.createdAt)}
                </div>
              </div>
            ) : null}
          </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex items-center justify-between flex-shrink-0 border-t border-gray-200">
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Fermer
              </button>
              <button
                onClick={handleExportPdf}
                disabled={isExporting}
                className="px-4 py-2 text-[#27489E] bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition flex items-center gap-2"
              >
                {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                {isExporting ? 'Génération...' : 'Générer la fiche PDF'}
              </button>
            </div>
            {vehicle && !vehicle.isVerified && (
              <div className="flex items-center gap-3">
                {vehicle.rejectionReason ? (
                  <>
                    <button
                      onClick={openRejectModal}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition flex items-center gap-2"
                    >
                      <XCircle className="w-4 h-4" />
                      Voir le motif de refus
                    </button>
                    <button
                      onClick={async () => {
                        if (window.confirm('Êtes-vous sûr de vouloir réinitialiser le statut de refus de ce véhicule ? Il pourra alors être vérifié à nouveau.')) {
                          try {
                            const response = await vehicleService.resetVehicleRejection(vehicle._id);
                            if (response.success) {
                              await loadVehicleDetails(); // Recharger les détails
                              
                              // Appeler le callback pour recharger la liste dans VehicleList
                              if (onResetRejection) {
                                onResetRejection(vehicle._id);
                              }
                              
                              alert('Statut de refus réinitialisé avec succès. Le véhicule peut maintenant être vérifié.');
                            } else {
                              alert(response.message || 'Erreur lors de la réinitialisation du statut de refus');
                            }
                          } catch (error) {
                            alert(error.message || 'Erreur lors de la réinitialisation du statut de refus');
                          }
                        }
                      }}
                      className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition flex items-center gap-2"
                    >
                      <Shield className="w-4 h-4" />
                      Réinitialiser le refus
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={openRejectModal}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition flex items-center gap-2"
                    >
                      <XCircle className="w-4 h-4" />
                      Refuser
                    </button>
                    <button
                      onClick={() => {
                        if (onVerify) {
                          onVerify(vehicle._id);
                        }
                        onClose();
                      }}
                      className="px-4 py-2 bg-[#27489E] text-white rounded-lg hover:bg-[#1e3a7a] transition flex items-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Vérifier le véhicule
                    </button>
                  </>
                )}
              </div>
            )}
        </div>
      </div>

      {/* Modal de refus */}
      {showRejectModal && (
        <div className="fixed inset-0 z-[60] overflow-hidden flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
            onClick={() => {
              setShowRejectModal(false);
              setRejectionMessage('');
              setRejectError('');
            }}
          />

          {/* Modal de refus */}
          <div className="relative bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all w-full max-w-2xl">
            {/* Header */}
            <div className="bg-red-500 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <h3 className="text-lg font-semibold text-white">Refuser le véhicule</h3>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionMessage('');
                  setRejectError('');
                }}
                className="text-white hover:text-gray-200 transition"
                disabled={isRejecting}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <p className="text-sm text-gray-600 mb-4">
                Choisissez un motif : le message se remplit avec la marque, le modèle et la plaque. Vous pouvez encore l’ajuster.
              </p>
              {rejectError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{rejectError}</p>
                </div>
              )}
              <div className="mb-4">
                <label htmlFor="rejectionReasonSelect" className="block text-sm font-semibold text-gray-700 mb-2">
                  Motif de rejet
                </label>
                <select
                  id="rejectionReasonSelect"
                  value={selectedRejectionReasonId}
                  onChange={(e) => applyRejectionReason(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 bg-white"
                  disabled={isRejecting}
                >
                  <option value="">Modèle par défaut</option>
                  {rejectionReasons.map((reason) => (
                    <option key={reason._id} value={reason._id}>
                      {reason.title}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="rejectionMessage" className="block text-sm font-semibold text-gray-700 mb-2">
                  Message envoyé à l’agence <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="rejectionMessage"
                  value={rejectionMessage}
                  onChange={(e) => setRejectionMessage(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all resize-none font-mono text-sm disabled:bg-gray-50 disabled:cursor-not-allowed"
                  rows={12}
                  required
                  disabled={isRejecting}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {rejectionMessage.length} caractères
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3 flex-shrink-0 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionMessage('');
                  setRejectError('');
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                disabled={isRejecting}
              >
                Annuler
              </button>
              <button
                onClick={async () => {
                  if (rejectionMessage.trim()) {
                    setIsRejecting(true);
                    setRejectError('');
                    try {
                      const response = await vehicleService.rejectVehicle(vehicle._id, rejectionMessage);
                      if (response.success) {
                        // Fermer les modals et recharger les détails
                        setShowRejectModal(false);
                        setRejectionMessage('');
                        await loadVehicleDetails(); // Recharger pour voir le nouveau statut
                        
                        // Appeler le callback pour recharger la liste dans VehicleList
                        if (onReject) {
                          onReject(vehicle._id);
                        }
                        
                        // Optionnel: fermer aussi le modal principal
                        // onClose();
                      } else {
                        setRejectError(response.message || 'Erreur lors du rejet du véhicule');
                      }
                    } catch (err) {
                      setRejectError(err.message || 'Erreur lors du rejet du véhicule');
                    } finally {
                      setIsRejecting(false);
                    }
                  }
                }}
                disabled={!rejectionMessage.trim() || isRejecting}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRejecting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Envoi...
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4" />
                    Confirmer le refus
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleDetailsModal;

